import { runInAction, set } from 'mobx';
import { observer } from 'mobx-react-lite';
import numeral from 'numeral';
import type { JSX } from 'react';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import type { OmniCampaign, Recommendation, TTDCampaign } from '@feathr/blackbox';
import { EmptyState, NumberInput } from '@feathr/components';
import ChannelBudgetSplit from '@feathr/extender/App/EventsPage/CampaignEditPage/AdWizard/AdWizardBudgetStep/ChannelBudgetSplit';
import type { IUseAudienceBreakdown } from '@feathr/report_components';

import RecommendationModal from './RecommendationModal';
import type { IUseChannelBudgetRecommendation } from './useChannelBudgetRecommendation';

interface IProps {
  campaign: TTDCampaign;
  recommendation: Recommendation;
  /** Budget-field validation errors, shown under the input. */
  errors: string[];
  /** Shared audience breakdown, used by the split card to label each channel. */
  audienceData: IUseAudienceBreakdown;
  /** Per-channel recommendation state from `useChannelBudgetRecommendation`. */
  budgetRecommendation: IUseChannelBudgetRecommendation;
}

/**
 * The proposed budget for the recommendation.
 *
 * Backend proposes `target_value` on single-channel campaigns. On dual-channel it
 * currently proposes only a channel split (Feathr/anhinga#7572 is still filling in
 * the split allocations), so there's no proposed budget to pre-fill — fall back to
 * the backend's total minimum, which is the number that actually resolves the
 * error. Returns 0 when neither is available yet.
 */
export function getProposedBudget(
  recommendation: Recommendation,
  channelMinimums: IUseChannelBudgetRecommendation['channelMinimums'],
): number {
  const proposed =
    recommendation.get('proposed_campaign_values')?.exposure_settings?.target_value ?? 0;
  return proposed || channelMinimums?.total || 0;
}

function UpdateBudget({
  campaign,
  recommendation,
  errors = [],
  audienceData,
  budgetRecommendation,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const {
    budgetExceedsAccountMax,
    channelMinimums,
    hasFailed,
    isLoading,
    recommendedAmounts,
    recommendedSplit,
  } = budgetRecommendation;

  const isDualChannel = campaign.isOmniCampaign && (campaign as OmniCampaign).isDualChannel;
  const currentBudget = campaign.get('exposure_settings')?.target_value ?? 0;
  const proposedBudget = getProposedBudget(recommendation, channelMinimums);

  /*
   * Pre-fill once, and only upward. The proposed budget can arrive after mount
   * (the dual-channel fallback waits on the minimums request), so this can't be a
   * bare mount effect — but it must not fight the user's own edits afterwards,
   * hence the ref.
   */
  const hasPrefilled = useRef(false);
  useEffect(() => {
    if (hasPrefilled.current || proposedBudget <= 0 || proposedBudget <= currentBudget) {
      return;
    }
    hasPrefilled.current = true;
    handleTargetValueChange(proposedBudget);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposedBudget]);

  function handleTargetValueChange(newValue?: number): void {
    const exposureSettings = campaign.get('exposure_settings');
    if (!exposureSettings) {
      return;
    }
    runInAction((): void => {
      set(exposureSettings, { target_value: newValue ?? 0 });
    });
    campaign.setAttributeDirty('exposure_settings');
  }

  /*
   * Without minimums there's nothing to divide the budget against, so show the
   * same empty state the wizard uses rather than a split the user can't trust.
   * Mirrors the wizard's `splitUnavailable`, including the account-max case where
   * no budget can satisfy the minimum.
   */
  const splitUnavailable =
    hasFailed ||
    budgetExceedsAccountMax ||
    channelMinimums === null ||
    currentBudget < channelMinimums.total;

  // Null minimums mean "not yet", not "unavailable" -- otherwise the empty state flashes.
  const hasResolved = channelMinimums !== null || hasFailed || budgetExceedsAccountMax;
  const isSettling = isLoading || audienceData.isLoading || !hasResolved;

  /*
   * Only shown when backend recorded a budget baseline. On dual-channel campaigns
   * it records the split instead, and "Original budget: $0" would be a lie.
   */
  const originalBudget =
    recommendation.get('current_campaign_values')?.exposure_settings?.target_value;

  let splitContent: JSX.Element;
  if (splitUnavailable && !isSettling) {
    splitContent = (
      <EmptyState
        description={t('We couldn’t calculate the channel minimums for this campaign.')}
        label={t('Channel split unavailable')}
        name={'channel_split_empty'}
        theme={'neutral'}
        width={'full'}
      />
    );
  } else {
    splitContent = (
      <ChannelBudgetSplit
        audienceData={audienceData}
        campaign={campaign as OmniCampaign}
        channelMinimums={channelMinimums}
        hideModeToggle={true}
        isRecommendationLoading={isSettling}
        recommendedAmounts={recommendedAmounts}
        recommendedSplit={recommendedSplit}
      />
    );
  }

  return (
    <>
      <RecommendationModal.Alert description={recommendation.get('message')} />
      <NumberInput
        // Applying a recommendation accepts the proposed values rather than editing them.
        disabled={true}
        helpPlacement={'bottom'}
        helpText={
          originalBudget
            ? t('Original budget: {{before}}', {
                before: numeral(originalBudget).format('$0,0'),
              })
            : undefined
        }
        // A disabled input never gains focus, and `Input` withholds errors until it has.
        initializeFocused={true}
        label={t('Budget')}
        min={channelMinimums?.total}
        name={'budget'}
        onChange={handleTargetValueChange}
        prefix={'$'}
        /* `errors` also carries in-flight save-blockers; painting those red flashes an
         * error the user can't act on. The save still blocks via `getValidationErrors`. */
        validationError={isLoading ? [] : errors}
        value={currentBudget}
      />
      {isDualChannel && splitContent}
    </>
  );
}

export default observer(UpdateBudget);
